(() => {
  const namespace = '__missionOwenSections';
  if (window[namespace]) {
    window[namespace].init(document);
    return;
  }

  const instances = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class MissionOwen {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.tabs = Array.from(root.querySelectorAll('[role="tab"][data-tab-target]'));
      this.panels = Array.from(root.querySelectorAll('[role="tabpanel"][data-tab-panel]'));
      this.modal = root.querySelector('[data-modal]');
      this.modalImage = root.querySelector('[data-modal-image]');
      this.modalClose = root.querySelector('[data-modal-close]');
      this.lastFocusedElement = null;
      this.previousBodyOverflow = '';
      this.revealObserver = null;
      this.sliderObserver = null;
      this.sliderTimer = null;
      this.currentSlide = 0;
      this.init();
    }

    init() {
      this.bindTabs();
      this.bindModal();
      this.bindSlider();
      this.bindThemeEditor();
      this.initReveal();
    }

    bindTabs() {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.tabTarget), { signal: this.signal });
        tab.addEventListener('keydown', (event) => {
          let nextIndex = null;

          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = this.tabs.length - 1;
          if (nextIndex === null) return;

          event.preventDefault();
          this.tabs[nextIndex].focus();
          this.activateTab(this.tabs[nextIndex].dataset.tabTarget, false);
        }, { signal: this.signal });
      });
    }

    activateTab(target, scroll = true) {
      const activeTab = this.tabs.find((tab) => tab.dataset.tabTarget === target);
      const activePanel = this.panels.find((panel) => panel.dataset.tabPanel === target);
      if (!activeTab || !activePanel) return;

      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const isActive = panel === activePanel;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
      });

      this.observeReveals(activePanel);
      if (target === 'donaciones') this.startSlider();
      else this.stopSlider();

      if (scroll) {
        const nav = this.root.querySelector('.mission-owen__nav-scroll');
        const top = nav ? nav.getBoundingClientRect().top + window.scrollY : this.root.offsetTop;
        window.scrollTo({ top, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    }

    bindModal() {
      if (!this.modal || !this.modalImage || !this.modalClose) return;

      this.root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-modal-trigger]');
        if (trigger && this.root.contains(trigger)) {
          this.openModal(trigger);
          return;
        }

        if (event.target === this.modal || event.target.closest('[data-modal-close]')) {
          this.closeModal();
        }
      }, { signal: this.signal });

      this.modal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeModal();
          return;
        }

        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.modal.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, { signal: this.signal });
    }

    openModal(trigger) {
      const source = trigger.dataset.fullSrc;
      if (!source) return;

      this.lastFocusedElement = trigger;
      this.previousBodyOverflow = document.body.style.overflow;
      this.modalImage.src = source;
      this.modalImage.alt = trigger.dataset.imageAlt || '';
      this.modal.hidden = false;
      document.body.style.overflow = 'hidden';
      this.modalClose.focus();
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      this.modalImage.removeAttribute('src');
      this.modalImage.alt = '';
      document.body.style.overflow = this.previousBodyOverflow;
      if (this.lastFocusedElement?.isConnected) this.lastFocusedElement.focus();
      this.lastFocusedElement = null;
    }

    bindSlider() {
      this.slider = this.root.querySelector('[data-slider]');
      if (!this.slider) return;

      this.sliderTrack = this.slider.querySelector('[data-slider-track]');
      this.slides = Array.from(this.slider.querySelectorAll('[data-slide]'));
      this.slideButtons = Array.from(this.slider.querySelectorAll('[data-slide-index]'));
      if (!this.sliderTrack || this.slides.length < 1) return;

      this.slideButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.goToSlide(Number(button.dataset.slideIndex));
          this.restartSlider();
        }, { signal: this.signal });
      });

      this.slider.addEventListener('mouseenter', () => this.stopSlider(), { signal: this.signal });
      this.slider.addEventListener('mouseleave', () => this.startSlider(), { signal: this.signal });
      this.slider.addEventListener('focusin', () => this.stopSlider(), { signal: this.signal });
      this.slider.addEventListener('focusout', (event) => {
        if (!this.slider.contains(event.relatedTarget)) this.startSlider();
      }, { signal: this.signal });

      this.sliderTrack.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.goToSlide(this.currentSlide + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal: this.signal });

      if ('IntersectionObserver' in window) {
        this.sliderObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = this.slides.indexOf(entry.target);
            if (index >= 0) this.updateSlideState(index);
          });
        }, { root: this.sliderTrack, threshold: 0.65 });
        this.slides.forEach((slide) => this.sliderObserver.observe(slide));
      }

      reduceMotion.addEventListener?.('change', () => this.restartSlider(), { signal: this.signal });
    }

    bindThemeEditor() {
      this.root.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('[data-tab-panel]');
        if (panel) this.activateTab(panel.dataset.tabPanel, false);

        const selectedSlide = event.target.closest('[data-slide]');
        if (selectedSlide && this.slides) {
          const index = this.slides.indexOf(selectedSlide);
          if (index >= 0) this.goToSlide(index);
        }
      }, { signal: this.signal });
    }

    goToSlide(index) {
      if (!this.slides?.length) return;
      const normalized = (index + this.slides.length) % this.slides.length;
      this.slides[normalized].scrollIntoView({
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
      this.updateSlideState(normalized);
    }

    updateSlideState(index) {
      this.currentSlide = index;
      this.slideButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === index;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', String(isActive));
      });
    }

    startSlider() {
      if (reduceMotion.matches || !this.slides || this.slides.length < 2 || this.sliderTimer) return;
      this.sliderTimer = window.setInterval(() => this.goToSlide(this.currentSlide + 1), 4000);
    }

    stopSlider() {
      if (!this.sliderTimer) return;
      window.clearInterval(this.sliderTimer);
      this.sliderTimer = null;
    }

    restartSlider() {
      this.stopSlider();
      this.startSlider();
    }

    initReveal() {
      if (reduceMotion.matches || !('IntersectionObserver' in window)) {
        this.root.querySelectorAll('[data-reveal]').forEach((element) => element.classList.add('is-visible'));
        return;
      }

      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -40px' });

      const activePanel = this.root.querySelector('[data-tab-panel]:not([hidden])');
      if (activePanel) this.observeReveals(activePanel);
    }

    observeReveals(container) {
      const elements = container.querySelectorAll('[data-reveal]');
      elements.forEach((element) => {
        if (reduceMotion.matches || !this.revealObserver) element.classList.add('is-visible');
        else this.revealObserver.observe(element);
      });
    }

    destroy() {
      this.closeModal();
      this.stopSlider();
      this.revealObserver?.disconnect();
      this.sliderObserver?.disconnect();
      this.controller.abort();
      instances.delete(this.root);
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-mission-owen]')) roots.push(scope);
    scope.querySelectorAll?.('[data-mission-owen]').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new MissionOwen(root));
    });
  };

  const unload = (scope) => {
    const root = scope.matches?.('[data-mission-owen]') ? scope : scope.querySelector?.('[data-mission-owen]');
    instances.get(root)?.destroy();
  };

  window[namespace] = { init, unload };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();
